// Theme loaders – load jQuery and Swiper on demand (no global load).
// Must be included in <head> without defer so sections can call these before body finishes parsing.

(function () {
  "use strict";

  window.jqueryCheckAndLoad = function () {
    if (window.jqueryCustomLoad) return Promise.resolve(window.jQuery);
    if (typeof window.jQuery !== "undefined") {
      window.jqueryCustomLoad = true;
      return Promise.resolve(window.jQuery);
    }
    window.jqueryCustomLoad = true;
    return new Promise(function (resolve) {
      var s = document.createElement("script");
      s.src = "https://code.jquery.com/jquery-3.7.1.min.js";
      s.onload = function () {
        resolve(window.jQuery);
      };
      s.onerror = function () {
        resolve(null);
      };
      document.head.appendChild(s);
    });
  };

  /**
   * Single place for dynamic pricing: apply quantity multiplication and optional subscription discount.
   * Subscription discounts are always applied per product (per unit); then result is multiplied by quantity.
   *
   * @param {object} opts
   * @param {string|number} [opts.variantId] - Variant ID (for API consistency; not used in calculation).
   * @param {number} opts.price - Base price in cents (e.g. variant.price, or per-unit when multiplyByQuantity is true).
   * @param {number} [opts.quantity=1] - Quantity (used when multiplyByQuantity is true; after subscription, total = perUnitSubscriptionPrice * quantity).
   * @param {boolean} [opts.multiplyByQuantity=true] - If true, base total = price * quantity before any subscription discount.
   * @param {boolean} [opts.applySubscriptionDiscount=true] - If true, apply discount from window.subscriptionContext when set (per product, then multiply by quantity).
   * @param {number} [opts.compareAtPrice] - Optional compare-at price in cents (multiply by quantity only; no subscription applied).
   * @returns {{ price: number, compareAtPrice: number }} Final price and compare-at in cents (use Elixir_FormatMoney for display).
   */
  window.Elixir_GetDisplayPrice = function (opts) {
    var price = Math.round(Number(opts.price) || 0);
    var quantity = Math.max(1, parseInt(opts.quantity, 10) || 1);
    var multiplyByQuantity = opts.multiplyByQuantity !== false;
    var applySubscriptionDiscount = opts.applySubscriptionDiscount !== false;
    var compareAtPrice = opts.compareAtPrice != null ? Math.round(Number(opts.compareAtPrice) || 0) : 0;

    var totalPrice = multiplyByQuantity ? price * quantity : price;
    var totalCompare = multiplyByQuantity ? compareAtPrice * quantity : compareAtPrice;

    if (applySubscriptionDiscount && window.subscriptionContext) {
      var ctx = window.subscriptionContext;
      var perUnitBase = Math.round(totalPrice / quantity);
      var discountType = ctx.discountType;
      var discountValue = parseFloat(ctx.discountValue) || 0;
      var finalPricePerUnit = ctx.finalPrice != null && ctx.finalPrice !== "" ? parseFloat(ctx.finalPrice) : null;

      /* ELIXIR PATCH (2026-08-23, Litterly): ONE PLAN PER RUNG (R-OFFER-17). The context
       * describes the ARMED rung, so applying it to every rung painted the selected rung's
       * per-unit across the whole ladder ($13.74/bottle on tiers 1, 2 and 3 while only tier 2
       * charges it). When the block publishes a per-quantity plan map, each rung is priced from
       * ITS OWN plan; without one this is inert and the context still rules. */
      if (typeof window.Elixir_PlanForQty === "function") {
        var _rung = window.Elixir_PlanForQty(quantity);
        var _adj = _rung && _rung.data && _rung.data.priceAdjustments && _rung.data.priceAdjustments[0];
        if (_adj) {
          /* Shopify applies a selling plan to the VARIANT price — never to our custom rung
           * price — so the base is the LIST per-unit (the rung's compare-at), not the rung's
           * own already-discounted per-unit. Using the latter double-discounts: tier 2 showed
           * $21.98 (45% off $19.99) where the plan really charges $27.48 (45% off $24.99). */
          /* FIXED 2026-09-15 (R-OFFER-15): the rung's compare-at is the variant price only while no
           * marketing anchor is set. With economics.unit_anchor above it the compare IS the
           * anchor, and the card advertised the plan off it -- $53.99 shown (10% off a
           * $59.99 anchor) against $35.99 charged, measured on staging. The context carries
           * the variant price the plan really applies to (originalPrice, the base the
           * per-unit line sync takes, R-OFFER-16); the old base is the fallback. */
          var _ctxBase = Math.round(Number(ctx.originalPrice) || 0);
          var _base = _ctxBase > 0 ? _ctxBase
            : (compareAtPrice > 0 ? compareAtPrice : perUnitBase);
          var _v = parseFloat(_adj.value) || 0;
          if (_adj.valueType === "percentage") finalPricePerUnit = Math.round(_base * (1 - _v / 100));
          else if (_adj.valueType === "fixed_amount") finalPricePerUnit = Math.round(_base - _v);
          else if (_adj.valueType === "price") finalPricePerUnit = Math.round(_v);
        }
      }

      var perUnitAfterDiscount;
      if (finalPricePerUnit !== null && !isNaN(finalPricePerUnit)) {
        perUnitAfterDiscount = Math.max(0, Math.round(finalPricePerUnit));
      } else if (discountType === "percentage") {
        perUnitAfterDiscount = Math.max(0, Math.round(perUnitBase * (1 - discountValue / 100)));
      } else if (discountType === "fixed_amount") {
        perUnitAfterDiscount = Math.max(0, Math.round(perUnitBase - discountValue));
      } else if (discountType === "price") {
        var perUnitCents = (finalPricePerUnit != null && !isNaN(finalPricePerUnit) ? finalPricePerUnit : discountValue);
        perUnitAfterDiscount = Math.max(0, Math.round(perUnitCents));
      } else {
        perUnitAfterDiscount = perUnitBase;
      }
      totalPrice = perUnitAfterDiscount * quantity;
      /* ELIXIR PATCH (2026-08-19, AMENDED 2026-08-23): the 2026-08-19 patch anchored the
       * SUBSCRIBED row to the ONE-TIME price of this same cart. That is right only while the
       * ladder itself gives no discount — then qty x list and the one-time total are the same
       * number. As soon as a rung discounts, the anchor COLLAPSES to the rung's own price and
       * the advertised flat depth becomes false: Litterly's 40% plan showed SAVE $10 (40%) at
       * tier 1, $10 (25%) at tier 2 and $5.01 (10%) at tier 3 under a "SAVE 40%" badge. Tier 1
       * is where the two anchors agree, which is why it read fine for three builds.
       * The subscribed row now anchors to THE SAME anchor the one-time row uses for that rung
       * (qty x list), so the depth is the advertised depth at every rung and the saving grows
       * with the tier — which is what BGV does ($10.26 / $29.01 / $41.70). Falls back to the
       * one-time total only when no usable anchor was passed, so the row is never left bare. */
      var oneTimeTotal = multiplyByQuantity ? price * quantity : price;
      if (!(totalCompare > totalPrice)) totalCompare = oneTimeTotal;
    }

    return { price: totalPrice, compareAtPrice: totalCompare };
  };

  /**
   * Set or clear the global subscription context (window.subscriptionContext).
   * Use this from EVENT HANDLERS AFTER PAGE LOAD (e.g. when the user toggles Subscribe, changes plan, or changes variant).
   * Same shape: originalPrice, priceWithDiscount, discountType, discountValue, finalPrice, sellingPlanId.
   * - With (planId, variant, plan): computes context from variant price + plan.priceAdjustments[0], sets window.subscriptionContext, dispatches subscriptionContext:updated.
   * - With no arguments (or planId/variant missing): clears context and dispatches subscriptionContext:updated with detail: { subscriptionContext: null }.
   *
   * @param {string|number} [planId] - Selling plan ID.
   * @param {object} [variant] - Variant object with .price (or .currentPrice) in cents.
   * @param {object} [plan] - Plan with .priceAdjustments: [{ valueType, value }] (same shape as __subscriptionPlansData.plans[]).
   */
  /* ELIXIR PATCH (2026-08-23, Litterly): the tier subtitle ("$X.XX per <unit>") is a static
   * block setting, so with the Subscribe toggle ON it kept showing the one-time per-unit price
   * next to a subscription total — misleading. In sub mode every tier's true per-unit is the
   * flat plan price (the plan reprices the variant; automatic discounts don't stack on
   * subscription lines — proven by cart reads), so sync the label from the context.
   * AMENDED 2026-09-15 (R-OFFER-16): any unit, not only "bottle". The first form matched and wrote
   * "per bottle", Litterly's noun, so a kit's line kept its one-time price beside a
   * subscribed total ("$35.99 per kit" by $67.98 for 2, measured on staging). A line in the
   * form R-OFFER-16 writes, "$X.XX per <unit>", keeps its unit exactly as written; a line in
   * any other form is never touched. */
  function elixirSyncPerUnitLabels() {
    var ctx = window.subscriptionContext;
    document.querySelectorAll(".quantity-break-option .quantity-break-savings").forEach(function (el) {
      var perUnit = /^\s*\$[\d,]*\.?\d+\s+[Pp]er\s+([A-Za-z](?:[A-Za-z '’-]*[A-Za-z])?)\s*$/;
      if (!el.dataset.elixirOrig && !perUnit.test(el.textContent)) return;
      if (!el.dataset.elixirOrig) el.dataset.elixirOrig = el.textContent;
      if (!ctx || ctx.finalPrice == null) { el.textContent = el.dataset.elixirOrig; return; }
      // each rung states ITS OWN subscribed per-unit, not the armed rung's (R-OFFER-17)
      var unit = ctx.finalPrice;
      var opt = el.closest(".quantity-break-option");
      var inp = opt && opt.querySelector("input.quantity-break-input");
      var q = inp ? (parseInt(inp.getAttribute("data-option-quantity") || inp.value, 10) || 1) : 1;
      if (typeof window.Elixir_PlanForQty === "function") {
        var r2 = window.Elixir_PlanForQty(q);
        var a2 = r2 && r2.data && r2.data.priceAdjustments && r2.data.priceAdjustments[0];
        var base = ctx.originalPrice || unit;   // the LIST per-unit the plan applies to
        if (a2) {
          var v2 = parseFloat(a2.value) || 0;
          if (a2.valueType === "percentage") unit = Math.round(base * (1 - v2 / 100));
          else if (a2.valueType === "fixed_amount") unit = Math.round(base - v2);
          else if (a2.valueType === "price") unit = Math.round(v2);
        }
      }
      var um = perUnit.exec(el.dataset.elixirOrig);
      if (!um) { el.textContent = el.dataset.elixirOrig; return; }
      el.textContent = "$" + (unit / 100).toFixed(2) + " per " + um[1];
    });
    /* ELIXIR PATCH (2026-08-23, Litterly): the buy-box PRICE ANCHOR under the H1 is a per-unit
     * display, and the page now LANDS subscribed (R-OFFER-8 preselect) — leaving it at the
     * one-time unit price puts a headline number on the page that nothing charges. The best
     * pages in David's n=52 teardown put the subscription price in the headline (Dr. Squatch
     * $36.00 "14% OFF", Spacemilk, BugMD). Same per-unit meaning as before, armed state aware. */
    document.querySelectorAll(".shop-product-price-block").forEach(function (el) {
      if (el.children.length) return;                 // only the plain single-price form
      if (!/^\s*\$[\d,]+\.\d{2}\s*$/.test(el.textContent) && !el.dataset.elixirOrig) return;
      if (!el.dataset.elixirOrig) el.dataset.elixirOrig = el.textContent;
      var want = ctx && ctx.finalPrice != null
        ? "$" + (ctx.finalPrice / 100).toFixed(2)
        : el.dataset.elixirOrig;
      if (el.textContent !== want) el.textContent = want;
      /* The anchor block runs its OWN price writer AFTER us (same clobber class as the
       * quantity-break tier prices), so a single write loses the race. Watch the node and
       * re-apply; the equality guard above makes this terminate. */
      if (!el.dataset.elixirWatched) {
        el.dataset.elixirWatched = "1";
        new MutationObserver(function () { elixirSyncPerUnitLabels(); })
          .observe(el, { childList: true, characterData: true, subtree: true });
      }
    });
  }

  window.Elixir_SetSubscriptionContext = function (planId, variant, plan) {
    if (planId == null || planId === "" || !variant) {
      if (window.subscriptionContext) {
        delete window.subscriptionContext;
      }
      elixirSyncPerUnitLabels();
      document.dispatchEvent(new CustomEvent("subscriptionContext:updated", {
        detail: { subscriptionContext: null }
      }));
      return;
    }

    var originalPrice = 0;
    if (typeof variant.price === "number") {
      originalPrice = variant.price;
    } else if (typeof variant.price === "string") {
      originalPrice = parseInt(variant.price, 10) || 0;
    } else if (variant.currentPrice != null) {
      originalPrice = typeof variant.currentPrice === "number" ? variant.currentPrice : parseInt(variant.currentPrice, 10) || 0;
    }

    var finalPrice = originalPrice;
    var discountType = null;
    var discountValue = null;

    if (plan && plan.priceAdjustments && plan.priceAdjustments.length > 0) {
      var adjustment = plan.priceAdjustments[0];
      var adjustmentValue = parseFloat(adjustment.value);
      discountType = adjustment.valueType;
      discountValue = adjustmentValue;

      switch (adjustment.valueType) {
        case "percentage":
          finalPrice = originalPrice * (1 - adjustmentValue / 100);
          break;
        case "fixed_amount":
          finalPrice = originalPrice - adjustmentValue;
          break;
        case "price":
          finalPrice = adjustmentValue;
          break;
        default:
          finalPrice = originalPrice;
          break;
      }
    }

    window.subscriptionContext = {
      originalPrice: Math.round(originalPrice),
      priceWithDiscount: Math.max(0, Math.round(finalPrice)),
      discountType: discountType,
      discountValue: discountValue,
      finalPrice: Math.max(0, Math.round(finalPrice)),
      sellingPlanId: String(planId)
    };

    elixirSyncPerUnitLabels();
    document.dispatchEvent(new CustomEvent("subscriptionContext:updated", {
      detail: { subscriptionContext: window.subscriptionContext }
    }));
  };

  /**
   * INITIAL PAGE LOAD ONLY: set window.subscriptionContext so Elixir_GetDisplayPrice works right away
   * (before any block like subscribe-and-save runs). Uses first plan + current variant from the form.
   * After load, plan changes are handled by Elixir_SetSubscriptionContext from subscribe-and-save.
   */
  function bootstrapSubscriptionContext() {
    var data = window.__subscriptionPlansData;
    if (!data || !data.plans || data.plans.length === 0) return;
    // ELIXIR PATCH (2026-08-23, Litterly): NEVER arm the context unless a subscribe toggle is
    // initially CHECKED. Without this guard, any product carrying a selling plan bootstraps
    // window.subscriptionContext on load even when the block preselects one-time -- every
    // quantity-break tier then displays the subscription price, and the add-to-cart items
    // builder attaches selling_plan, silently enrolling a one-time buyer. Measured live:
    // toggle OFF, ATC on the 2-bottle tier -> cart line carried selling_plan 4907729119.
    var _tg = document.querySelector('.subscribe-toggle-switch input, .subscribe-toggle-dynamic');
    if (!_tg || !_tg.checked) return;

    var activePlan = data.plans[0];
    var variants = window.__productVariants;
    if (!Array.isArray(variants) || variants.length === 0) return;

    var variantId = null;
    if (typeof window.Elixir_GetProductFormVariantId === "function") {
      variantId = window.Elixir_GetProductFormVariantId();
    }
    if (!variantId && typeof document !== "undefined") {
      var form = typeof window.Elixir_GetProductForm === "function" && window.Elixir_GetProductForm();
      if (form) {
        var idInput = form.querySelector('input[name="id"]');
        if (idInput && idInput.value) variantId = idInput.value;
      }
    }
    var variant = null;
    if (variantId != null && variantId !== "") {
      for (var j = 0; j < variants.length; j++) {
        if (String(variants[j].id) === String(variantId)) {
          variant = variants[j];
          break;
        }
      }
    }
    if (!variant) variant = variants[0];

    window.Elixir_SetSubscriptionContext(activePlan.id, variant, activePlan);
  }

  if (typeof document !== "undefined") {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", bootstrapSubscriptionContext);
    } else {
      bootstrapSubscriptionContext();
    }
  }

  /**
   * Format price in cents to the store's money string. Use after Liquid when variant or other changes update prices in JS.
   * @param {number} cents - Price in cents (e.g. variant.price)
   * @param {string} [formatString] - Optional. Shopify format e.g. '${{amount}}'. Uses Shopify.money_format if omitted.
   * @returns {string} Formatted price string
   */
  window.Elixir_FormatMoney = function (cents, formatString) {
    var amount = Math.round(Number(cents) || 0);
    var format = formatString || (typeof Shopify !== "undefined" && (Shopify.money_format || Shopify.money_without_currency_format)) || "{{amount}}";
    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(amount, format);
    }
    var normalized = (amount / 100).toFixed(2);
    if (typeof format === "string") return format.replace(/\{\{amount\}\}/g, normalized);
    return "$" + normalized;
  };

  window.swiperCheckAndLoad = function () {
    if (window.swiperCustomLoad && typeof window.Swiper !== "undefined") {
      return Promise.resolve();
    }
    if (window.swiperLoadPromise) return window.swiperLoadPromise;
    window.swiperCustomLoad = true;
    var link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css";
    document.head.appendChild(link);
    var script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js";
    window.swiperLoadPromise = new Promise(function (resolve) {
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        resolve();
      };
    });
    document.head.appendChild(script);
    return window.swiperLoadPromise;
  };
})();
